/**
 * The icon set. One stroke weight, one grid, one file.
 *
 * Inline SVG rather than an icon package: the app needs about twenty glyphs
 * and they must inherit `currentColor` and the token stroke width to sit right
 * next to the type. A dependency would ship five hundred.
 */
import type { SVGProps } from 'react';

import { cn } from '@/lib/cn';

const PATHS = {
  /* Navigation */
  board: 'M4 5h5v14H4zm11 0h5v9h-5z',
  work: 'M12 3 3 8l9 5 9-5zM3 13l9 5 9-5M3 17.5 12 22l9-4.5',
  people: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m-6 9v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1M16.5 5.2a3.5 3.5 0 0 1 0 6.6M18 14.2A5 5 0 0 1 21 19v1',
  capacity: 'M3 20V10m5 10V4m5 16v-7m5 7V8',
  hours: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18m0-14v5l3.5 2',
  review: 'M3 6h18M3 12h10M3 18h6m5 1 2.5 2.5L21 15',
  dashboard: 'M3 17.5 9 11l4 3.5L21 6M21 6h-5m5 0v5',

  /* Interface */
  menu: 'M4 7h16M4 12h16M4 17h16',
  close: 'm6 6 12 12M18 6 6 18',
  chevronDown: 'm6 9 6 6 6-6',
  chevronRight: 'm9 6 6 6-6 6',
  chevronLeft: 'm15 6-6 6 6 6',
  check: 'm5 13 4 4L19 7',
  plus: 'M12 5v14M5 12h14',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16m6.5-1.5L21 21',
  alert: 'M12 3.5 22 20H2zM12 10v4m0 3h.01',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18m0-9v5m0-8h.01',
  mail: 'M3 6h18v12H3zm0 .5 9 6.5 9-6.5',
  key: 'M14.5 3a6.5 6.5 0 0 0-6.2 8.5L3 16.8V21h4.2l1.3-1.3v-2h2v-2h2l1.2-1.2A6.5 6.5 0 1 0 14.5 3m2.2 4.3h.01',
  signOut: 'M15 17v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v2m3 10 4-5-4-5m4 5H9',
  refresh: 'M20 11A8 8 0 0 0 6.3 6.3L4 8.5M4 13a8 8 0 0 0 13.7 4.7L20 15.5M4 4v4.5h4.5M20 20v-4.5h-4.5',
  inbox: 'M3 13h5l1.5 3h5L16 13h5M3 13l2.5-8h13L21 13v6H3z',
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10m0-14v2m0 14v2M3 12h2m14 0h2M5.6 5.6 7 7m10 10 1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4',
  moon: 'M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5',
  monitor: 'M3 5h18v11H3zM8 20h8m-4-4v4',
  external: 'M14 4h6v6m0-6-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5',
  compass: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18m3.5-12.5-2 5.5-5.5 2 2-5.5z',
} as const;

export type IconName = keyof typeof PATHS;

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  /** Pixel size. Matches the surrounding text size by default. */
  size?: number;
}

export function Icon({ name, size = 16, className, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      // Icons in this app are always beside their label, so they are decoration.
      aria-hidden="true"
      focusable="false"
      className={cn('shrink-0', className)}
      {...rest}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
